/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-redeclare */
import {
  CreateTechnicInput as CreateInput,
  CreateTechnicMutationVariables as CreateMutationVariables,
  CustomValue,
  DeleteTechnicMutationVariables as DeleteMutationVariables,
  EntityType,
  FileCategory,
  File as FileModel,
  Model,
  OnCreateTechnicSubscription,
  OnDeleteTechnicSubscription,
  OnUpdateTechnicSubscription,
  RepartitionKey,
  S3Object,
  addTechnicMaintenanceHistoryProperties,
  Technic,
  TechnicType,
  UtilityHistoryDTO,
  cleanInputCreate,
  cleanInputUpdate,
  cleanTechnicMaintenanceHistory,
  getReadId,
  getTableClientId,
  isNilOrEmpty,
  onCreateTechnic,
  onDeleteTechnic,
  onUpdateTechnic,
  resolveOneToOne,
  todayDateTime,
} from '@rentguru/commons-utils';
import {
  cleanMixedDatastoreAndAPISubscription,
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import { toast } from '@up2rent/ui-new';
import isNil from 'lodash/isNil';
import React, { useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { fetchFilesAndGetS3ObjectUrls, useFiles } from './FilesContext';
import { useNotifications } from './NotificationsContext';
import { fetchTechnicRepartitionKeys, useRepartitionKeys } from './RepartitionKeyContext';
import { useUser } from './UserContext';
import {
  createTechnic as createMutation,
  deleteTechnic as deleteMutation,
  getTechnic as getQuery,
  syncTechnics,
  syncTechnicsForFolders,
  updateTechnic as updateMutation,
} from './utils/cleanedQueries';
import { FileCategoryTypeForm } from './utils/fileUtils';
import { Permissions, usePermissions } from './utils/PermissionsContext';
import { type TechnicUpdate, updateTechnicsOrRestore as runTechnicUpdatesOrRestore } from './utils/technicsUpdateUtils';

const ENTITY_MODEL_NAME: Model = 'Technic';

const customSyncTechnics = addTechnicMaintenanceHistoryProperties(syncTechnics);
const customSyncTechnicsForFolders = addTechnicMaintenanceHistoryProperties(syncTechnicsForFolders);

export type ActionTechnic =
  | {
      type: 'SHOULD_FETCH_TECHNIC' | 'IS_FETCHING_TECHNIC';
    }
  | {
      type: 'ADD_TECHNIC';
      payload: { technic: Technic };
    }
  | {
      type: 'FETCHED_TECHNIC';
      payload: { technics: Technic[] };
    }
  | {
      type: 'UPDATE_TECHNIC';
      payload: { technic: Technic };
    }
  | {
      type: 'DELETE_TECHNIC';
      payload: { id: string };
    };

export interface ClassifiedTechnics {
  utilityProviders: Technic[];
  PEBs: Technic[];
  detectors: Technic[];
  heatings: Technic[];
  chimneys: Technic[];
  fuelTanks: Technic[];
}

export const fromFileCategoryToTechnic = (category: FileCategory) => {
  if (category === FileCategory.HYDROCARBONTANK_CERTIFICATE) return TechnicType.FUELTANK;
  if (category === FileCategory.PEB) return TechnicType.PEB;
  if (category === FileCategory.HEATING) return TechnicType.HEATING;
  if (category === FileCategory.UTILITY) return TechnicType.UTILITY_PROVIDER;
  if (category === FileCategory.CHIMNEY) return TechnicType.CHIMNEY;
  if (category === FileCategory.DETECTOR) return TechnicType.DETECTOR;
};

export const fromTechnicTypeToFileCategory = (type: TechnicType) => {
  if (type === TechnicType.FUELTANK) return FileCategory.HYDROCARBONTANK_CERTIFICATE;
  if (type === TechnicType.PEB) return FileCategory.PEB;
  if (type === TechnicType.HEATING) return FileCategory.HEATING;
  if (type === TechnicType.UTILITY_PROVIDER) return FileCategory.UTILITY;
  if (type === TechnicType.CHIMNEY) return FileCategory.CHIMNEY;
  if (type === TechnicType.DETECTOR) return FileCategory.DETECTOR;
};

export const isTechnicDeletionAllowed = (
  technic: Pick<Technic, 'type'>,
  permissions: Pick<Permissions, 'buildingsUnitsTechnicsDelete' | 'buildingsUnitsUtilityProvidersDelete'>
): boolean =>
  technic.type === TechnicType.UTILITY_PROVIDER
    ? permissions.buildingsUnitsUtilityProvidersDelete
    : permissions.buildingsUnitsTechnicsDelete;

export const attachFilesToTechnic = async (technics: Technic[]) => {
  const filesPromises = technics.map((technic) => fetchFilesAndGetS3ObjectUrls(EntityType.TECHNIC, technic.id));
  const filesResults = await Promise.all(filesPromises);

  const technicsWithFiles = technics.map((technic) => {
    const maintenanceFiles = filesResults.find((files) =>
      files.some((file) => file.foreignKey === technic.id && file.foreignTableName === EntityType.TECHNIC)
    );
    if (maintenanceFiles) {
      return { ...technic, files: maintenanceFiles };
    }

    return technic;
  });

  return technicsWithFiles;
};

export const groupFilesAndTechnicsByCategory = (
  files: S3Object[][],
  technics: (Technic | undefined)[]
): FileCategoryTypeForm[] => {
  return files.reduce((acc: FileCategoryTypeForm[], files: S3Object[], i: number) => {
    const filesTechnic = !isNil(technics[i]) ? technics[i]! : {};
    for (const file of files) {
      const index = acc.findIndex((filesWithCategory) => filesWithCategory.category === file.category);
      if (index > -1) {
        acc[index].files.push(file);
        acc[index] = { ...filesTechnic, ...acc[index] };
      } else {
        acc.push({ ...filesTechnic, category: file.category!, files: [file] });
      }
    }
    return acc;
  }, []);
};

export const classifyTechnics = (technics: Technic[]): ClassifiedTechnics => {
  return technics.reduce(
    (acc: ClassifiedTechnics, technic: Technic) => {
      if (technic.type === TechnicType.UTILITY_PROVIDER) {
        acc.utilityProviders.push(technic);
      } else if (technic.type === TechnicType.PEB) {
        acc.PEBs.push(technic);
      } else if (technic.type === TechnicType.DETECTOR) {
        acc.detectors.push(technic);
      } else if (technic.type === TechnicType.HEATING) {
        acc.heatings.push(technic);
      } else if (technic.type === TechnicType.CHIMNEY) {
        acc.chimneys.push(technic);
      } else if (technic.type === TechnicType.FUELTANK) {
        acc.fuelTanks.push(technic);
      }
      return acc;
    },
    { utilityProviders: [], PEBs: [], detectors: [], heatings: [], chimneys: [], fuelTanks: [] }
  );
};

const cleanTechnicInput = (input: Partial<Technic>): Partial<Technic> => {
  const {
    contact: _contact,
    utilityDistributor: _utilityDistributor,
    building: _building,
    unit: _unit,
    lease: _lease,
    files: _files,
    customValues,
    maintenanceHistory,
    utilityHistory,
    ...cleanedObject
  } = input;

  const result: Partial<Technic> = { ...cleanedObject };

  if (input.hasOwnProperty('customValues')) {
    result.customValues = Array.isArray(customValues)
      ? customValues
          .filter((customValue) => typeof customValue?.name === 'string' && customValue.name.trim() !== '')
          .map(
            (customValue): CustomValue => ({
              name: customValue.name,
              value: customValue.value == null ? '' : String(customValue.value),
            })
          )
      : (customValues as CustomValue[] | undefined | null);
  }

  if (input.hasOwnProperty('maintenanceHistory')) {
    result.maintenanceHistory = maintenanceHistory?.map(cleanTechnicMaintenanceHistory);
  }

  if (input.hasOwnProperty('utilityHistory')) {
    result.utilityHistory = (utilityHistory as UtilityHistoryDTO[] | null | undefined)?.map(
      ({ __typename, ...history }) => history
    );
  }

  return result;
};

export interface TechnicsContext {
  technics: Technic[];
  technicsWithoutLeasesLink: Technic[]; // Technics filtered to remove all linked technic to leaseId
  getTechnic: (id: string) => Technic | undefined;
  getTechnicsFor: (unitId: string) => Technic[];
  createTechnic: (
    input: Omit<Technic, 'id' | 'clientId' | 'readId'> & Partial<Pick<Technic, 'id'>>
  ) => Promise<Technic>;
  updateTechnic: (original: Technic, updates: Partial<Technic>) => Promise<Technic>;
  updateTechnicsOrRestore: (updates: TechnicUpdate[]) => Promise<Technic[]>;
  deleteTechnic: (id: string) => Promise<Technic | undefined>;
  /** Undoes a technic created in the same save: no delete right needed, since creating it was allowed. */
  discardCreatedTechnic: (id: string) => Promise<void>;
  deleteTechnicAndFiles: (id: string) => Promise<boolean>;
  deepDeleteTechnic: (technic: Technic, alreadyNetworkData?: boolean) => Promise<void>;
  technicsLoading: boolean;
  technicsError: string | undefined;
  setFetchTechnic: () => void;
}

export const technicReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_TECHNIC':
      if (state.Technic.loading || state.Technic.shouldFetch) {
        return state;
      }
      return {
        ...state,
        Technic: { ...state.Technic, shouldFetch: true },
      };
    case 'IS_FETCHING_TECHNIC':
      if (state.Technic.loading) {
        return state;
      }
      return {
        ...state,
        Technic: { ...state.Technic, loading: true },
      };
    case 'FETCHED_TECHNIC':
      return {
        ...state,
        Technic: {
          ...state.Technic,
          data: action.payload.technics,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_TECHNIC':
      // Check if already present - If already added by this user or coming from another user
      if (state.Technic.data?.find((object) => object.id === action.payload.technic.id)) {
        return state;
      }

      return {
        ...state,
        Technic: {
          ...state.Technic,
          data: [...state.Technic.data, action.payload.technic],
        },
      };
    case 'UPDATE_TECHNIC':
      // No data
      if (isNilOrEmpty(state.Technic.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.Technic.data?.find((object) => object.id === action.payload.technic.id);
      if (!currentObject) {
        return {
          ...state,
          Technic: {
            ...state.Technic,
            data: [...state.Technic.data, action.payload.technic],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.technic)) {
        return state;
      }

      // Update
      return {
        ...state,
        Technic: {
          ...state.Technic,
          data: state.Technic.data.map((object) => {
            if (object.id === action.payload.technic.id) {
              return action.payload.technic;
            }
            return object;
          }),
        },
      };
    case 'DELETE_TECHNIC':
      if (isNilOrEmpty(state.Technic.data)) {
        return state;
      }
      return {
        ...state,
        Technic: {
          ...state.Technic,
          data: state.Technic.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

const fetchTechnics = async (
  by: 'byUnitId' | 'byLeaseId' | 'byClientId',
  byValue: string,
  additionalFilter?: object
): Promise<Technic[]> => {
  return await list<Technic>(customSyncTechnics, getFilterFieldNameForIndex(by), byValue, additionalFilter);
};

export const fetchTechnicsForFolders = async (clientId: string): Promise<Technic[]> => {
  return await list<Technic>(
    customSyncTechnicsForFolders,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME)
  );
};

// eslint-disable-next-line no-redeclare
export const TechnicsContext = React.createContext<TechnicsContext | null>(null);

export const TechnicsContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    Technic: { data: technicsLoader, loading: technicsLoading, shouldFetch },
    Contact: { data: contactsLoader, loading: contactsLoading },
    Building: { data: buildingsLoader, loading: buildingsLoading },
    Unit: { data: unitsLoader, loading: unitsLoading },
    Lease: { data: leasesLoader, loading: leasesLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const permissions = usePermissions();
  const { deleteFile, fetchFiles } = useFiles();
  const { deleteRepartitionKey } = useRepartitionKeys(false);
  const { notifications, deleteNotification, notificationsLoading } = useNotifications();
  const { clientId, isOwner, userId } = useUser();
  const { formatMessage } = useIntl();

  const loading =
    technicsLoading || contactsLoading || buildingsLoading || unitsLoading || leasesLoading || notificationsLoading;

  const technics = useMemo<Technic[]>(() => {
    if (loading) {
      return [];
    }
    const technicsMapped = technicsLoader.map((technic) => {
      const utilityHistory = technic.utilityHistory ?? [];
      return {
        ...technic,
        utilityHistory,
        ...(technic.buildingId ? { building: resolveOneToOne(technic.buildingId, buildingsLoader, 'id') } : {}),
        ...(technic.unitId ? { unit: resolveOneToOne(technic.unitId, unitsLoader, 'id') } : {}),
        ...(technic.leaseId ? { lease: resolveOneToOne(technic.leaseId, leasesLoader, 'id') } : {}),
        ...(technic.utilityDistributorId
          ? { utilityDistributor: resolveOneToOne(technic.utilityDistributorId, contactsLoader, 'id') }
          : {}),
        ...(technic.contactId ? { contact: resolveOneToOne(technic.contactId, contactsLoader, 'id') } : {}),
      };
    });
    // adding new technic on lease implies to create 2 technics.
    // one for the lease and one for the unit.
    // first one for tenant, second for owner.
    // The owner technics shall thus not contain any leaseId.
    return technicsMapped;
  }, [technicsLoader, loading, buildingsLoader, unitsLoader, leasesLoader, contactsLoader]);

  const setFetchTechnic = () => {
    contextDispatch({ type: 'SHOULD_FETCH_TECHNIC' });
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_TECHNIC' });
      try {
        const result = await fetchTechnics('byClientId', getTableClientId(clientId!, ENTITY_MODEL_NAME));
        contextDispatch({ type: 'FETCHED_TECHNIC', payload: { technics: result } });
      } catch (error) {
        console.error('[technics] fetching technics failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'Technic' } });
      }
    };
    if (shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  useSubscriptions<OnCreateTechnicSubscription, OnUpdateTechnicSubscription, OnDeleteTechnicSubscription>(
    cleanMixedDatastoreAndAPISubscription(onCreateTechnic),
    cleanMixedDatastoreAndAPISubscription(onUpdateTechnic),
    cleanMixedDatastoreAndAPISubscription(onDeleteTechnic),
    (data) => {
      contextDispatch({
        type: 'ADD_TECHNIC',
        payload: { technic: data.onCreateTechnic as Technic },
      });
    },
    (data) => {
      contextDispatch({
        type: 'UPDATE_TECHNIC',
        payload: { technic: data.onUpdateTechnic as Technic },
      });
    },
    (data) => {
      const { id } = data.onDeleteTechnic as Technic;
      contextDispatch({
        type: 'DELETE_TECHNIC',
        payload: { id },
      });
    },
    undefined,
    undefined,
    undefined,
    true
  );

  const createTechnic = async (input: Omit<Technic, 'id' | 'clientId' | 'readId'> & Partial<Pick<Technic, 'id'>>) => {
    const cleanedObject = cleanTechnicInput(input);
    const technic = await mutation<Technic, CreateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(cleanedObject) as CreateInput),
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME),
        ...(isOwner && { writers: [userId!] }),
      },
    });
    contextDispatch({ type: 'ADD_TECHNIC', payload: { technic } });
    return technic;
  };

  const updateTechnic = async (original: Technic, updates: Partial<Technic>) => {
    const cleanedObject = cleanTechnicInput(updates);
    const result = await mutation<Technic>(updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...cleanedObject }, false) },
    });
    contextDispatch({ type: 'UPDATE_TECHNIC', payload: { technic: result } });
    return result;
  };

  const discardCreatedTechnic = async (id: string): Promise<void> => {
    await deleteEntityWithFetchBefore<Pick<Technic, 'id'>, DeleteMutationVariables>({ id }, getQuery, deleteMutation);
    contextDispatch({ type: 'DELETE_TECHNIC', payload: { id } });
  };

  const deleteTechnic = async (id: string) => {
    const technic = getTechnic(id);
    if (!technic) {
      return;
    }
    if (!isTechnicDeletionAllowed(technic, permissions)) {
      return technic;
    }

    const linkedTechnics = technics.filter((candidate) => (candidate.certificateIds ?? []).includes(id));
    await Promise.all(
      linkedTechnics.map((linkedTechnic) =>
        updateTechnic(linkedTechnic, {
          certificateIds: (linkedTechnic.certificateIds ?? []).filter((certificateId) => certificateId !== id),
        })
      )
    );

    await discardCreatedTechnic(id);
    return technic;
  };

  const deleteTechnicAndFiles = async (id: string): Promise<boolean> => {
    const technic = getTechnic(id);
    if (isNil(technic) || !isTechnicDeletionAllowed(technic, permissions)) {
      return false;
    }
    try {
      const technicFiles = await fetchFiles(EntityType.TECHNIC, id);
      await deleteTechnic(id);
      await Promise.all(technicFiles.map((technicFile) => deleteFile(technicFile)));
      return true;
    } catch (error) {
      console.error('[technics] deleting the technic and its files failed', error);
      toast.error(formatMessage({ id: 'error.deleteFile' }));
      return false;
    }
  };

  const getTechnic = (id: string): Technic | undefined => (loading ? undefined : technics.find((u) => u.id === id));

  const getTechnicsFor = (id: string): Technic[] =>
    loading
      ? []
      : technics.filter(
          (t) => (t.unitId === id && isNil(t.leaseId)) || (t.buildingId === id && isNil(t.leaseId)) || t.leaseId === id
        );

  const deepDeleteTechnic = async (technic: Technic, filesAlreadyIncluded = false) => {
    if (!isTechnicDeletionAllowed(technic, permissions)) {
      return;
    }
    const deletePromises: Promise<Technic | FileModel | RepartitionKey | void>[] = [];
    const technicFiles = filesAlreadyIncluded ? technic.files! : await fetchFiles(EntityType.TECHNIC, technic.id);
    for (const technicFile of technicFiles as FileModel[]) {
      deletePromises.push(deleteFile(technicFile));
    }
    const technicRepartitionKeys = await fetchTechnicRepartitionKeys(technic.id, clientId!);
    for (const technicRepartitionKey of technicRepartitionKeys) {
      deletePromises.push(deleteRepartitionKey(technicRepartitionKey));
    }
    const technicNotifications = notifications.filter(
      (notification) => notification.technic && notification.technic.id === technic.id
    );
    for (const technicNotification of technicNotifications) {
      deletePromises.push(deleteNotification(technicNotification.id));
    }

    deletePromises.push(deleteTechnic(technic.id));
    await Promise.all(deletePromises);
  };

  // Adding new technic on lease implies to create 2 technics: 1 for lease (tenant) and 1 for unit (manager/owner).
  // The manager technics shall thus not contain any leaseId.
  const technicsWithoutLeasesLink = technics.filter((technic) => isNil(technic.leaseId));

  const values = useMemo(
    () => ({
      technicsWithoutLeasesLink,
      technics,
      getTechnic,
      getTechnicsFor,
      createTechnic,
      updateTechnic,
      updateTechnicsOrRestore: (updates: TechnicUpdate[]) => runTechnicUpdatesOrRestore(updates, updateTechnic),
      deleteTechnic,
      discardCreatedTechnic,
      deleteTechnicAndFiles,
      deepDeleteTechnic,
      technicsError: undefined,
      technicsLoading: loading,
      setFetchTechnic,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [technics, loading]
  );

  return <TechnicsContext.Provider value={values}>{children}</TechnicsContext.Provider>;
};

export const useTechnics = (): TechnicsContext => {
  const context = useContext<TechnicsContext | null>(TechnicsContext);

  if (context === undefined) {
    throw new Error('`useTechnics` hook must be used within a `TechnicsContextProvider` component');
  }
  return context as TechnicsContext;
};
